import Carousel from "react-grid-carousel"
import styled, { css } from "styled-components"
import { pdfjs, Document, Page } from "react-pdf"
import React, { useEffect, useState } from "react"
import { useToaster } from "@hellocontento/maillard"

import Loader from "components/common/loading/Loader"

import "react-pdf/dist/esm/Page/AnnotationLayer.css"
import "utils/pdfjsWorker"

const options = {
  cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@4.2.67/standard_fonts`,
}

interface IArrowBtn {
  direction: "right" | "left"
}

const Arrow = styled.span<IArrowBtn>`
  display: inline-block;
  position: absolute;
  top: 50%;
  transform: ${({ direction }) =>
    `translateY(-50%) rotate(${direction === "right" ? "45deg" : "-135deg"})`};
  width: 16px;
  height: 16px;
  cursor: pointer;
  border-top: 2px solid ${props => props.theme.colors.grey06};
  border-right: 2px solid ${props => props.theme.colors.grey06};
`

const ArrowWrapper = styled.div<IArrowBtn>`
  display: flex;
  position: absolute;
  top: 0;
  z-index: 9;
  width: 30px;
  height: 100%;
  cursor: pointer;

  ${({ direction }) =>
    direction === "left"
      ? css`
          left: 0px;
          justify-content: flex-end;
        `
      : css`
          right: 0px;
          justify-content: flex-start;
        `};

  &:hover {
    background-color: ${props => props.theme.colors.grey01};

    ${Arrow} {
      border-color: ${props => props.theme.colors.black};
    }
  }
`

const ArrowBtn = ({ direction }: IArrowBtn) => (
  <ArrowWrapper direction={direction}>
    <Arrow direction={direction} />
  </ArrowWrapper>
)

const PageNumber = styled.span<{ page: number; totalPages: number }>`
  position: static;

  &:before {
    position: absolute;
    top: 5px;
    left: 5px;
    content: "${({ page, totalPages }) => `${page} of ${totalPages}`}";
    font-size: 12px;
    color: ${props => props.theme.colors.text04};
    background: transparent;
  }
`

const Wrapper = styled.div`
  overflow: hidden;
  width: 100%;

  /* react-grid-carousel adds 20px side margins on the rail wrapper to make room
     for default arrows. Our arrows are absolutely positioned overlays, so kill
     the margins to let the PDF fill edge-to-edge. */
  .react-pdf__Document > div > div {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  /* Scale the canvas to fill the available width regardless of PDF native dimensions.
     The react-pdf Page component sets inline width/height in pixels based on the
     native PDF size. Override both the wrapper div and the canvas so the page
     stretches edge-to-edge without white side bars. */
  .react-pdf__Page {
    width: 100% !important;
    height: auto !important;
  }
  .react-pdf__Page canvas {
    width: 100% !important;
    height: auto !important;
  }
`

interface PdfCarouselProps {
  url: string
}

const PdfCarousel: React.FC<PdfCarouselProps> = ({ url }) => {
  const addToast = useToaster()
  // Pre-fetch via the main thread (which has the correct page origin for CORS).
  // The PDF.js worker is loaded from a CDN and has origin: null, so it cannot
  // fetch cross-origin GCS URLs directly even when CORS is configured.
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    setBlobUrl(null)
    setError(null)
    setTotalPages(0)

    let objectUrl: string
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.blob()
      })
      .then(blob => {
        objectUrl = URL.createObjectURL(blob)
        setBlobUrl(objectUrl)
      })
      .catch(err => {
        console.error("Failed to load PDF carousel", { url, err })
        setError(err)
        addToast("PDF could not be loaded.", "error")
      })

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url, addToast])

  const onLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages)
  }

  const onLoadError = (err: unknown) => {
    console.error("Failed to parse PDF carousel", { url, err })
    setError(err)
    addToast("PDF could not be loaded.", "error")
  }

  if (error) return null

  if (!blobUrl) return <Loader location="center" />

  return (
    <Wrapper>
      <Document
        file={blobUrl}
        loading={<Loader location="center" />}
        onLoadSuccess={onLoadSuccess}
        onLoadError={onLoadError}
        options={options}
      >
        <Carousel
          cols={1}
          rows={1}
          gap={0}
          hideArrow={totalPages <= 1}
          mobileBreakpoint={0}
          arrowLeft={<ArrowBtn direction="left" />}
          arrowRight={<ArrowBtn direction="right" />}
        >
          {totalPages === 0 ? (
            <Carousel.Item>
              <Loader location="center" />
            </Carousel.Item>
          ) : (
            [...Array(totalPages)].fill(0).map((_, index) => (
              <Carousel.Item key={`page_${index + 1}`}>
                <Page
                  loading={<Loader location="center" />}
                  pageNumber={index + 1}
                  width={undefined}
                />
              </Carousel.Item>
            ))
          )}
        </Carousel>
      </Document>
    </Wrapper>
  )
}

export default PdfCarousel
